import { useEffect, useId, useRef, useState } from 'react';

import {
    Button,
    cn,
    focusRingOnDark,
    Icon,
    IconButton,
    MonoLabel,
    ProgressBar,
} from '@/design-system';

import { useShellTopBar } from '@/shared/ui';

import { surveyCopy } from '../copy';

/** Een paneel dat de balk opent: open of dicht, en welk element het is. */
export interface HeaderPanel {
    open: boolean;
    onToggle: () => void;
    controls: string;
}

export interface SurveyHeaderProps {
    /** Waar het onderdeel onder hangt: "25 · Daken"; leeg bij een hoofdonderdeel. */
    eyebrow: string;
    /** Het onderdeel: "Diverse dak". */
    title: string;
    answered: number;
    visible: number;
    percentage: number;
    online: boolean;
    /** Wat het synchroniseren doet zolang er verbinding is: "2 wijzigingen wachten", "Alles gesynchroniseerd". */
    syncText?: string;
    /**
     * "Nu versturen", zoals bij Afronden: alleen met verbinding en als er werk van
     * deze opname wacht. Zonder staat alleen de status in het venster.
     */
    onSyncNow?: (() => void) | undefined;
    /**
     * Springt naar de eerste vraag die nog open staat. `null` zodra alles
     * beantwoord is. De teller zelf is die knop: de inspecteur tikt op het getal
     * dat hem stoort en staat bij het werk dat er nog ligt.
     */
    onNextIncomplete?: (() => void) | null;
    /** Alleen als de boom niet naast de vragen past: de titel opent hem. */
    tree: HeaderPanel | null;
    /** Alleen als de bevindingen niet naast de vragen passen. */
    findings: HeaderPanel | null;
    /** Het menu met de keuring, "Eerst onvolledig", "Afronden" en de weg terug. */
    menu: HeaderPanel;
    /** Een smal scherm (iPad staand) krijgt de gewone kantlijn van 16px. */
    narrow?: boolean;
}

/**
 * Eén rustige balk boven de opname, plus de 3px voortgangsbalk.
 *
 * Wat de inspecteur elke minuut nodig heeft, staat erin: waar hij is, hoe ver hij
 * is en of zijn werk bewaard en verstuurd is. Het onderdeel krijgt de ruimte: een
 * duidelijke titel die op twee regels mag, met het onderdeel erboven klein en
 * afgekapt. Of alles verstuurd is, zegt één stip; een tik (of de muis erop) toont
 * de hele status en "Nu versturen". Offline is amber, nooit rood: de inspecteur
 * doet niets fout als er geen netwerk is (principe 1). De rest - het adres,
 * "Eerst onvolledig", "Afronden" en de weg terug - staat een tik verder in het
 * menu. De balk blijft op elke iPad-breedte één regel; alles erin is 44px. Op een
 * telefoon (balk smaller dan 600 px) krijgen stip, teller en bevindingen een
 * tweede regel, zodat de titel leesbaar blijft.
 *
 * Op een smal scherm staat er geen balk van de schil boven: de knop Menu van de
 * schil staat dan links in deze balk (`useShellTopBar`).
 */
export function SurveyHeader({
    eyebrow,
    title,
    answered,
    visible,
    percentage,
    online,
    syncText,
    onSyncNow,
    tree,
    findings,
    menu,
    narrow = false,
    onNextIncomplete = null,
}: SurveyHeaderProps) {
    const shellMenu = useShellTopBar();

    return (
        <>
            <header className="@container bg-[var(--ink-800)]">
                <div
                    className={cn(
                        'flex min-h-16 flex-wrap items-center gap-x-3 gap-y-1 py-[10px]',
                        narrow ? 'px-4' : 'px-[22px]',
                    )}
                >
                    {shellMenu ? (
                        <IconButton
                            variant="onDark"
                            size="md"
                            icon="menu"
                            aria-label={surveyCopy.header.appMenu}
                            aria-expanded={shellMenu.open}
                            aria-controls={shellMenu.controls}
                            onClick={shellMenu.toggle}
                        />
                    ) : null}

                    <div className="relative flex min-h-11 min-w-0 flex-1 basis-0 items-center gap-2">
                        <div className="min-w-0">
                            {eyebrow ? (
                                <MonoLabel
                                    size={10}
                                    tone="onDarkMuted"
                                    as="div"
                                    className="mb-[3px] truncate"
                                >
                                    {eyebrow}
                                </MonoLabel>
                            ) : null}
                            {/* Een lange naam loopt door op een tweede regel en wordt pas daarna afgekapt. */}
                            <h1 className="m-0 line-clamp-2 font-[family-name:var(--font-display)] text-[19px] leading-[1.2] font-semibold tracking-[-0.015em] break-words text-[var(--text-on-dark)]">
                                {title}
                            </h1>
                        </div>

                        {tree ? (
                            <>
                                <Icon name="chevronDown" size="md" tone="onDarkMuted" />
                                {/* De hele plek is de knop: tik op waar je bent om ergens anders heen te gaan. */}
                                <button
                                    type="button"
                                    aria-label={surveyCopy.panels.topics}
                                    aria-expanded={tree.open}
                                    aria-controls={tree.controls}
                                    onClick={tree.onToggle}
                                    className={cn(
                                        'absolute inset-0 cursor-pointer rounded-[7px] border-0 bg-transparent',
                                        focusRingOnDark,
                                    )}
                                />
                            </>
                        ) : null}
                    </div>

                    {/* Op een telefoon een eigen regel: stip, teller en bevindingen. */}
                    <div className="flex shrink-0 items-center gap-3 @max-[600px]:order-last @max-[600px]:w-full">
                        <SyncStatus online={online} syncText={syncText} onSyncNow={onSyncNow} />

                        <Counter
                            answered={answered}
                            visible={visible}
                            onNextIncomplete={onNextIncomplete ?? null}
                        />

                        {findings ? (
                            <Button
                                variant="onDark"
                                size="md"
                                aria-expanded={findings.open}
                                aria-controls={findings.controls}
                                onClick={findings.onToggle}
                                className="shrink-0 @max-[600px]:ml-auto"
                                /* Kort op een telefoon, zodat "Meer" (met Afronden) in de balk blijft passen. */
                                {...(narrow ? { 'aria-label': surveyCopy.panels.findings } : {})}
                            >
                                {narrow
                                    ? surveyCopy.panels.findingsShort
                                    : surveyCopy.panels.findings}
                            </Button>
                        ) : null}
                    </div>

                    <IconButton
                        variant="onDark"
                        size="md"
                        icon="more"
                        aria-label={surveyCopy.menu.open}
                        aria-expanded={menu.open}
                        aria-controls={menu.controls}
                        onClick={menu.onToggle}
                    />
                </div>
            </header>

            <ProgressBar
                size="thin"
                surface="dark"
                value={percentage}
                label={surveyCopy.header.progressLabel}
            />
        </>
    );
}

/**
 * Hoe ver de opname is, en tegelijk de weg naar wat er nog ligt: tikken op het
 * getal brengt de inspecteur bij de eerste vraag die nog open staat. Zolang er
 * niets meer open staat is het gewoon een getal — een knop die nergens heen gaat
 * is erger dan geen knop.
 */
function Counter({
    answered,
    visible,
    onNextIncomplete,
}: {
    answered: number;
    visible: number;
    onNextIncomplete: (() => void) | null;
}) {
    const numbers = (
        <>
            <MonoLabel size={10} tone="onDarkMuted" as="div" aria-hidden="true">
                {surveyCopy.header.counterLabel}
            </MonoLabel>
            <div
                aria-hidden="true"
                className="mt-[3px] flex items-center gap-1 font-[family-name:var(--font-display)] text-[15px] leading-[1.25] font-semibold whitespace-nowrap text-[var(--text-on-dark)]"
            >
                {answered} / {visible}
                {onNextIncomplete ? <NextArrow /> : null}
            </div>
        </>
    );

    if (!onNextIncomplete) {
        return (
            <div
                className="shrink-0 text-right @max-[600px]:text-left"
                aria-label={surveyCopy.header.counter(answered, visible)}
                role="group"
            >
                {numbers}
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={onNextIncomplete}
            aria-label={`${surveyCopy.header.counter(answered, visible)} · ${surveyCopy.header.nextIncomplete}`}
            className="-my-1 flex min-h-11 shrink-0 cursor-pointer flex-col justify-center rounded-[7px] border-0 bg-transparent px-2 text-right hover:bg-[var(--ink-700)] @max-[600px]:text-left"
        >
            {numbers}
        </button>
    );
}

/** Hetzelfde teken als op een recorder: door naar wat nog niet af is. */
function NextArrow() {
    return (
        <svg viewBox="0 0 12 12" className="h-3 w-3" fill="currentColor" aria-hidden="true">
            <path d="M2 1.5 8 6l-6 4.5z" />
            <rect x="8.8" y="1.5" width="1.6" height="9" rx="0.4" />
        </svg>
    );
}

/**
 * Of het werk verstuurd is, als één stip in de balk: accentkleur met verbinding,
 * amber zonder - nooit rood. Dezelfde maat in beide toestanden, zodat de balk niet
 * verspringt zodra het netwerk wegvalt. De stip pulseert hier niet: de enige
 * animatie in de app is de sync-indicator in de schil.
 *
 * De hele status staat in de naam van de knop en in een venster dat een tik (of
 * de muis erop) opent, met "Nu versturen" als er iets wacht. Een verandering wordt
 * voorgelezen, net als bij de oude pil.
 */
function SyncStatus({
    online,
    syncText,
    onSyncNow,
}: {
    online: boolean;
    syncText?: string | undefined;
    onSyncNow?: (() => void) | undefined;
}) {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const panelId = useId();
    const wrapper = useRef<HTMLDivElement | null>(null);
    const button = useRef<HTMLButtonElement | null>(null);
    const status = online ? (syncText ?? surveyCopy.header.online) : surveyCopy.header.offline;
    const shown = open || hovered;

    useEffect(() => {
        if (!shown) {
            return;
        }

        const closeOutside = (event: PointerEvent) => {
            if (!wrapper.current?.contains(event.target as Node)) {
                setOpen(false);
                setHovered(false);
            }
        };

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
                setHovered(false);
                button.current?.focus();
            }
        };

        document.addEventListener('pointerdown', closeOutside);
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.removeEventListener('pointerdown', closeOutside);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [shown]);

    return (
        <div
            ref={wrapper}
            className="relative shrink-0"
            onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') {
                    setHovered(true);
                }
            }}
            onPointerLeave={(event) => {
                if (event.pointerType === 'mouse') {
                    setHovered(false);
                }
            }}
        >
            <button
                ref={button}
                type="button"
                aria-label={surveyCopy.header.syncStatus(status)}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen((current) => !current)}
                className={cn(
                    'flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent',
                    'hover:bg-[var(--ink-hover-on-dark)]',
                    focusRingOnDark,
                )}
            >
                <span
                    aria-hidden="true"
                    className="block h-[10px] w-[10px] rounded-full"
                    style={
                        online
                            ? { background: 'var(--accent)' }
                            : {
                                  background: 'var(--warn-field)',
                                  boxShadow: '0 0 0 4px var(--warn-on-dark-surface)',
                              }
                    }
                />
            </button>

            {/* Een verandering van status wordt voorgelezen, ook met het venster dicht. */}
            <span aria-live="polite" className="sr-only">
                {status}
            </span>

            {shown ? (
                <div
                    id={panelId}
                    role="group"
                    aria-label={surveyCopy.header.syncTitle}
                    className="absolute top-full left-1/2 z-30 w-[260px] -translate-x-1/2 pt-2 @max-[600px]:left-0 @max-[600px]:translate-x-0"
                >
                    <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] p-[14px] shadow-[var(--shadow-popover)]">
                        <MonoLabel size={10} tone="muted" as="div" className="mb-[6px]">
                            {surveyCopy.header.syncTitle}
                        </MonoLabel>
                        <p className="m-0 flex items-start gap-2 text-[14px] leading-[1.4] text-[var(--text-primary)]">
                            <span
                                aria-hidden="true"
                                className="mt-[5px] block h-2 w-2 shrink-0 rounded-full"
                                style={{ background: online ? 'var(--accent)' : 'var(--warn)' }}
                            />
                            <span className={online ? undefined : 'text-[var(--warn-text)]'}>
                                {status}
                            </span>
                        </p>
                        {/* Waarom er geen knop Opslaan is: die is er al, bij elke tik. */}
                        <p className="m-0 mt-[6px] text-[12px] leading-[1.45] text-[var(--text-secondary)]">
                            {surveyCopy.header.autoSaved}
                        </p>
                        {online && onSyncNow ? (
                            <Button
                                variant="outline"
                                size="md"
                                fullWidth
                                className="mt-3"
                                onClick={() => {
                                    onSyncNow();
                                    setOpen(false);
                                    setHovered(false);
                                    button.current?.focus();
                                }}
                            >
                                {surveyCopy.header.syncNow}
                            </Button>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
